import React from "react";
import { Card, CardContent } from "./ui/card";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { MapPin, Clock, ArrowRight, Star } from "lucide-react";
import { mockData } from "../mock";

export const Mentors = () => {
  return (
    <section id="mentors" className="py-20 bg-white">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center space-y-4 mb-16">
          <h2
            id="mentor-heading" // ✅ TARGET FOR SCROLL
            className="text-4xl font-bold text-gray-900"
          >
            Learn from Experienced Industry Professionals
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Our mentors are experienced professionals from established
            technology organizations, offering structured guidance, in-depth
            code reviews, and career-oriented mentorship throughout the program.
          </p>
        </div>

        {/* Mentors Grid */}
        <div className="grid md:grid-cols-3 gap-8 mb-16">
          {mockData.mentors.map((mentor) => (
            <Card
              key={mentor.id}
              className="group bg-white hover:shadow-xl transition-all duration-300 hover:-translate-y-1 overflow-hidden"
            >
              <CardContent className="p-0">
                {/* Mentor Image */}
                <div className="relative h-64 overflow-hidden">
                  <img
                    src={mentor.image}
                    alt={mentor.name}
                    className="w-[350px] object-center transition-transform duration-300 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                </div>

                {/* Mentor Info */}
                <div className="p-6 space-y-4">
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">
                      {mentor.name}
                    </h3>
                    <p className="text-blue-600 font-medium">{mentor.role}</p>
                    <div className="flex items-center gap-2 text-gray-600 mt-1">
                      <MapPin size={14} />
                      <span className="text-sm">{mentor.company}</span>
                    </div>
                  </div>

                  {/* Expertise Tags */}
                  <div className="space-y-2">
                    <p className="text-sm font-medium text-gray-700">
                      Expertise:
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {mentor.expertise.map((skill, idx) => (
                        <Badge key={idx} variant="outline" className="text-xs">
                          {skill}
                        </Badge>
                      ))}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Mentor Features */}
        <div className="grid md:grid-cols-3 gap-8 mb-16">
          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="p-6 text-center space-y-4">
              <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center mx-auto">
                <Clock className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900">
                Weekly One-on-One Mentorship Sessions
              </h3>
              <p className="text-gray-700">
                Scheduled one-on-one mentoring sessions to review progress,
                address challenges, and define a structured learning roadmap.
              </p>
            </CardContent>
          </Card>

          <Card className="bg-green-50 border-green-200">
            <CardContent className="p-6 text-center space-y-4">
              <div className="w-12 h-12 bg-green-600 rounded-full flex items-center justify-center mx-auto">
                <Star className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900">
                Structured Code Reviews
              </h3>
              <p className="text-gray-700">
                Bi-weekly professional code reviews focused on improving code
                quality, maintainability, and adherence to industry standards.
              </p>
            </CardContent>
          </Card>

          <Card className="bg-purple-50 border-purple-200">
            <CardContent className="p-6 text-center space-y-4">
              <div className="w-12 h-12 bg-purple-600 rounded-full flex items-center justify-center mx-auto">
                <ArrowRight className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900">
                Career and Placement Guidance
              </h3>
              <p className="text-gray-700">
                Comprehensive career support including interview preparation,
                resume optimization, and structured referrals within our hiring
                network.
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Mentor Stats */}
        <div className="bg-gray-900 text-white rounded-2xl p-8 mb-16">
          <div className="text-center mb-8">
            <h3 className="text-2xl font-bold mb-2">
              Proven Mentor-Driven Outcomes
            </h3>
            <p className="text-gray-300">
              Our mentor-driven model ensures focused individual attention,
              consistent feedback, and guidance aligned with real-world industry
              expectations.
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-8 text-center">
            <div>
              <div className="text-3xl font-bold text-blue-400">1:1</div>
              <p className="text-gray-300">Mentor to Learner Ratio</p>
            </div>
            <div>
              <div className="text-3xl font-bold text-green-400">10+</div>
              <p className="text-gray-300">Years Average Industry Experience</p>
            </div>
            <div>
              <div className="text-3xl font-bold text-purple-400">Weekly</div>
              <p className="text-gray-300">Code Review Cycles</p>
            </div>
            <div>
              <div className="text-3xl font-bold text-orange-400">Regular</div>
              <p className="text-gray-300">Slack-Based Support Access</p>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="text-center space-y-6">
          <h3 className="text-2xl font-bold text-gray-900">
            Connect with a Program Mentor?
          </h3>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Schedule a complimentary 10-minute consultation with one of our
            mentors to discuss your goals, receive professional guidance, and
            assess program suitability.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              size="lg"
              type="button"
              className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-6 text-lg"
              onClick={() =>
                window.open(
                  "https://api.whatsapp.com/send?phone=919895223307",
                  "_blank"
                )
              }
            >
              Schedule a Mentor Consultation
              <ArrowRight className="ml-2" size={20} />
            </Button>

            {/* ✅ UPDATED BUTTON */}
            <Button
              variant="outline"
              size="lg"
              className="px-8 py-6 text-lg border-2"
              onClick={() =>
                document
                  .getElementById("mentor-heading")
                  ?.scrollIntoView({ behavior: "smooth" })
              }
            >
              View Mentor Profiles
            </Button>
          </div>

          <p className="text-sm text-gray-500">
            No obligation • Complimentary 10-minute consultation • Personalized
            professional guidance
          </p>
        </div>
      </div>
    </section>
  );
};
